import { useEffect, useState } from 'react';
import { shallowEqual } from 'react-redux';
import ListViewHOC from 'components/ListView';
import DefaultListView from 'components/ListView/DefaultListView';
import LoadingWrapper from 'components/LoadingWrapper';
import { useCustomerNavigate } from 'hooks/useCustomerNavigate';
import { useObjectIdRouteParam } from 'hooks/useObjectIdRouteParam';
import {
  createM2MUser,
  deleteM2MUser,
  fetchM2MUsers,
  saveM2MUser,
  userSelector,
  usersLoading,
  usersSelector,
} from 'slices/apiKeys.slice';
import { userSelector as authUserSelector } from 'slices/auth.slice';
import { fetchLanguages } from 'slices/language.slice';
import { fetchOrganizationHierarchy } from 'slices/organizationHierarchy.slice';
import { fetchPermissions } from 'slices/permission.slice';
import { fetchRoles } from 'slices/roles.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import { EUserVisibility, type TM2MUser } from '@timeedit/types';
import { Button, DefaultContentLayout } from '@timeedit/ui-components';
import ApiKeyDetailPage from './detail';

const ApiKeyPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useCustomerNavigate();
  const userId = useObjectIdRouteParam();

  /**
   * SELECTORS
   */
  const authUser = useAppSelector(authUserSelector);
  const users = useAppSelector(usersSelector, shallowEqual);
  const user = useAppSelector(userSelector(userId));
  const isLoading = useAppSelector(usersLoading);
  const [filterData, setFilterData] = useState<any[]>([]);
  const [sortingData, setSortingData] = useState<any[]>([]);
  const [apiKeyList, setApiKeyList] = useState<any[]>([]);
  const [filteredApiKeyList, setFilteredApiKeyList] =
    useState<any[]>(apiKeyList);
  const [M2MUser, setM2MUser] = useState<TM2MUser>(user);

  useEffect(() => {
    if (userId !== M2MUser?.id) {
      setM2MUser(user);
    }
  }, [userId]);

  useEffect(() => {
    dispatch(fetchM2MUsers());
    dispatch(fetchLanguages());
    dispatch(fetchPermissions());
    dispatch(fetchOrganizationHierarchy());
    dispatch(fetchRoles());
  }, [dispatch]);

  useEffect(() => {
    setApiKeyList(
      users.map((user) => ({
        listLabel: user.name,
        value: user.id,
        inactiveSince: !!user.inactiveSince,
      }))
    );
  }, [users]);

  /**
   * Filter and sorting
   */

  useEffect(() => {
    setFilterData([
      {
        property: 'inactiveSince',
        label: 'Status',
        type: 'boolean',
        options: [
          { name: 'Inactive', value: true },
          { name: 'Active', value: false },
          { name: 'All', value: 'all' },
        ],
        value: false,
      },
    ]);

    setSortingData([
      {
        property: 'listLabel',
        label: 'Name',
        order: 'DESC',
        type: 'text',
        active: true,
      },
    ]);
  }, [users]);

  /**
   * EFFECTS
   */

  useEffect(() => {
    // Jumping to newly saved new user
    if (
      users.length > 0 &&
      userId === 'new' &&
      users.findIndex((el) => el.id === 'new') === -1
    ) {
      navigate(`/api-services/api-keys/${users[users.length - 1].id}`, {
        replace: true,
      });
    }
    // Default case
    else if (!userId && users && users[0] && users[0].id) {
      navigate(`/api-services/api-keys/${users[0].id}`, { replace: true });
    } else if (userId && !user) {
      navigate('/api-services/api-keys/', { replace: true });
    }
  }, [userId, user, users, navigate]);

  // EVENT HANDLERS
  const onSelectUser = (userId: string) => {
    navigate(`/api-services/api-keys/${userId}`);
  };

  // Adds new api key which has not yet been saved
  const onAddUser = () => {
    dispatch(createM2MUser());
    onSelectUser('new');
  };

  const onFilterAndSortChange = (
    list: any[],
    filterData: any,
    sortingData: any
  ) => {
    setFilterData(filterData);
    setSortingData(sortingData);
    setFilteredApiKeyList(list);
  };

  const onDeleteUser = (userIdToDelete: string) => {
    dispatch(deleteM2MUser(userIdToDelete));
  };

  const onSaveUser = (apiKey: TM2MUser) => {
    dispatch(saveM2MUser(apiKey));
  };

  const isUserEditable = (apiKey: TM2MUser) => {
    return (
      apiKey?.visibility === EUserVisibility.READ_ONLY &&
      !(
        authUser.scopes.includes('TE_ROOT_USER') ||
        authUser.scopes.includes('TE_ADMIN::super')
      )
    );
  };

  return (
    <DefaultContentLayout
      title="General Settings"
      subtitle="General settings for organization"
      actions={
        <>
          <Button onClick={() => setM2MUser(user)}>{'Discard'}</Button>
          <Button type="primary" onClick={() => onSaveUser(M2MUser)}>
            {'Save'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', height: '100%', marginTop: 10 }}>
        <ListViewHOC
          sectionHeading=""
          number={apiKeyList.length}
          onSelect={onAddUser}
          onFilterAndSortChange={onFilterAndSortChange}
          listData={apiKeyList}
          filterData={filterData}
          sortingData={sortingData}
          searchProp={'listLabel'}
        >
          <DefaultListView
            data={apiKeyList.length > 0 ? filteredApiKeyList : []}
            onSelect={onSelectUser}
            onDelete={onDeleteUser}
            selectedItemId={userId}
            isLoading={isLoading}
          />
        </ListViewHOC>
        <div className="inner-content--wrapper allow-scroll">
          <LoadingWrapper isLoading={isLoading}>
            {userId && M2MUser && (
              <ApiKeyDetailPage
                isReadOnly={isUserEditable(user)}
                user={M2MUser}
                setM2MUser={setM2MUser}
              />
            )}
          </LoadingWrapper>
        </div>
      </div>
    </DefaultContentLayout>
  );
};

export default ApiKeyPage;
